import {
  Skeleton,
  Stack,
  SxProps,
  Tab,
  Tabs,
  Theme,
  Tooltip,
  Typography,
  TypographyProps,
} from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { Counter } from './Counter';

type TabsWithCountersProps<T> = {
  tabItems: {
    labelId: string;
    label?: React.ReactNode;
    value: T;
    count?: number;
    icon?: React.ReactNode;
    tooltipContent?: React.ReactNode;
    tabTestId?: string;
  }[];
  onChange: (event: React.SyntheticEvent, newValue: T) => void;
  activeTab: T;
  sx?: SxProps<Theme>;
  textVariant?: TypographyProps['variant'];
  isLoading?: boolean;
};

export const TabsWithCounters = <T,>({
  tabItems,
  onChange,
  activeTab,
  isLoading,
  sx,
  textVariant = 'h3',
}: TabsWithCountersProps<T>) => {
  const [labelWidth, setLabelWidth] = useState(0);
  const { formatMessage } = useIntl();

  const measureActiveLabel = useCallback((el: HTMLElement | null) => {
    if (el) setLabelWidth(el.offsetWidth);
  }, []);

  return (
    <Tabs
      value={activeTab}
      onChange={onChange}
      TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
      sx={(theme) => ({
        minHeight: 0,
        '& .MuiTab-root': {
          minHeight: 0,
          '&:not(:last-child)': { mr: 3 },
        },
        '.MuiTab-root:not(.Mui-selected)': {
          '& .text': { color: 'common.grey' },
        },
        '& .MuiTabs-indicator': {
          display: 'flex',
          backgroundColor: 'transparent',
        },
        '& .MuiTabs-indicatorSpan': {
          maxWidth: labelWidth,
          width: '100%',
          backgroundColor: isLoading ? 'transparent !important' : 'primary.main',
          transition: 'all 0.3s',
        },
        ...((typeof sx === 'function' ? sx(theme) : sx) as SystemStyleObject),
      })}
    >
      {tabItems.map(({ labelId, label, value, count, icon, tooltipContent, tabTestId }) => {
        const isActive = activeTab === value;
        const tabLabel = formatMessage({ id: labelId });
        const text = label ?? (
          <Typography
            className="text"
            variant={textVariant}
            pl={icon ? 0.5 : 0}
            sx={isLoading ? { position: 'relative', whiteSpace: 'nowrap' } : undefined}
          >
            {isLoading ? (
              <>
                <span style={{ visibility: 'hidden' }}>{tabLabel}</span>
                <Skeleton
                  variant="rectangular"
                  sx={{ position: 'absolute', inset: 0, transform: 'none' }}
                />
              </>
            ) : (
              formatMessage({ id: labelId })
            )}
          </Typography>
        );

        return (
          <Tab
            key={labelId}
            value={value}
            {...(tabTestId ? { 'data-test-id': tabTestId } : {})}
            label={
              <Stack
                direction="row"
                alignItems="center"
                ref={isActive ? measureActiveLabel : undefined}
              >
                {icon}
                {tooltipContent ? (
                  <Tooltip
                    title={tooltipContent}
                    componentsProps={{
                      tooltip: {
                        sx: { width: 200, maxHeight: 450, overflow: 'auto', padding: 1.25 },
                      },
                    }}
                  >
                    <div>{text}</div>
                  </Tooltip>
                ) : (
                  text
                )}
              </Stack>
            }
            icon={
              count ? (
                <Counter
                  inverse={!isActive}
                  shape="circle"
                  sx={(theme) => ({
                    border: `1px solid ${theme.palette.primary.main}`,
                    ...(!isActive && {
                      borderColor: 'common.grey',
                      color: 'common.grey',
                    }),
                  })}
                >
                  {count}
                </Counter>
              ) : undefined
            }
            iconPosition="end"
          />
        );
      })}
    </Tabs>
  );
};
